/**
 * Presentational FAQ accordion.
 *
 * Renders the questions only — it emits no structured data. Use this when a
 * page shows several FAQ groups but must publish a single FAQPage node (a page
 * with multiple FAQPage nodes is ambiguous to crawlers). For the common case of
 * one FAQ block per page, use {@link FAQSection}, which wraps this component
 * and emits the schema for you.
 */

'use client';

import React, { useState } from 'react';
import type { FAQItem } from '@/components/seo';
import { ChevronDown } from 'lucide-react';

export interface FAQAccordionProps {
  faqs: FAQItem[];
  /** Heading rendered above the questions. Omit to render the list bare. */
  title?: string;
  description?: string;
  /** Open the first question on mount. */
  defaultOpen?: boolean;
  /**
   * Distinguishes the heading/answer element ids when a page renders more than
   * one accordion, so `aria-controls` stays unique across groups.
   */
  idPrefix?: string;
}

export function FAQAccordion({
  faqs,
  title,
  description,
  defaultOpen = false,
  idPrefix = 'faq',
}: FAQAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen ? 0 : null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Generate stable key from question text (fallback to index for edge cases)
  const getFAQKey = (faq: FAQItem, index: number): string => {
    // Create a simple hash from question text for stable keys
    const questionHash = faq.question
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
    return `${idPrefix}-${questionHash}-${index}`;
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFAQ(index);
    }
  };

  const headingId = `${idPrefix}-heading`;

  return (
    <section aria-labelledby={title ? headingId : undefined} className="mb-16">
      {title && (
        <div className="text-center mb-12">
          <h2 id={headingId} className="text-3xl md:text-4xl font-bold mb-4 text-white">
            {title}
          </h2>
          {description && (
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">{description}</p>
          )}
        </div>
      )}
      <div className="max-w-3xl mx-auto space-y-4">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          const answerId = `${idPrefix}-answer-${index}`;
          return (
            <div
              key={getFAQKey(faq, index)}
              className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 overflow-hidden transition-all duration-300 hover:border-border"
            >
              <button
                onClick={() => toggleFAQ(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                aria-expanded={isOpen}
                aria-controls={answerId}
              >
                <h3 className="text-lg md:text-xl font-bold text-foreground pr-8 flex-1">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`h-5 w-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={answerId}
                className={`overflow-hidden transition-all duration-300 ${
                  isOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}
                aria-hidden={!isOpen}
              >
                <div
                  className={`px-6 pb-4 border-t transition-colors duration-300 ${
                    isOpen ? 'border-border/50 pt-4' : 'border-transparent pt-0'
                  }`}
                >
                  <p className="text-foreground leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
